<script setup lang="ts">
definePageMeta({
  name: 'feedback-detail',
})
defineRouteRules({ cache: false })

import {
  finishFeedbackGrade,
  getFeedbackDetail,
  getFeedbackReportReasons,
  type FeedbackDetailData,
  type FeedbackReportReason,
} from '~/api/feedback'
import { aRgbToRgba } from '~/utils/formatters'
import { FALLBACK_DOWNLOAD_LINKS, getAppDownloadLinks } from '~/api/download'

const appStore = useAppStore()
const userIdCookie = useAppCookie('userid')

const route = useRoute()

const { t, localePath, locale } = useAppI18n()
const { getBreadcrumb, setBreadcrumb } = useLsBreadcrumb()

type BreadcrumbSourceItem = {
  route: string
  label?: string
  labelKey?: string
}

const isReportDialogOpen = ref(false)
/** 反馈问题已解决确认弹窗是否打开。 */
const isFeedbackResolvedDialogOpen = ref(false)
/** 是否正在提交口碑结案请求 */
const isSubmittingResolve = ref(false)
/** 是否已进入客户端挂载阶段，用于避免 isSelf 服务端和客户端不一致导致闪烁。 */
const isClientMounted = ref(false)
const sourceBreadcrumb = ref<BreadcrumbSourceItem | null>(null)
const reportReasons = ref<FeedbackReportReason[]>([])

/** 是否展示翻译后的反馈内容，点击“查看原文”区域时切换。 */
const isTranslatedContentVisible = ref(true)

/** 当前主图对应的图片下标。 */
const selectedFeedbackImageIndex = ref(0)

/** 缩略图横向滚动容器。 */
const feedbackThumbnailScrollerRef = ref<HTMLElement | null>(null)

/** 缩略图真实内容轨道。 */
const feedbackThumbnailTrackRef = ref<HTMLElement | null>(null)

/** 按当前语言解析来源面包屑文案。 */
const resolveSourceBreadcrumbLabel = (breadcrumb: BreadcrumbSourceItem | null) => {
  if (!breadcrumb) {
    return ''
  }

  if (breadcrumb.labelKey) {
    return t(breadcrumb.labelKey)
  }

  return breadcrumb.label || ''
}

/** 反馈详情页面包屑配置。 */
const breadcrumbItems = computed(() => {
  const exchangeAlias =
    feedbackDetailData.value?.webBitWebBottom?.alias ||
    feedbackDetailData.value?.tokenGradeCommentInfo?.evaluateCode ||
    ''
  const exchangeSourceBreadcrumb: BreadcrumbSourceItem | null = exchangeAlias
    ? {
        route: localePath(`/exchanges/${exchangeAlias}`),
        label:
          feedbackDetailData.value?.tokenGradeCommentInfo?.evaluateName ||
          feedbackDetailData.value?.tokenGradeCommentInfo?.localEvaluateName ||
          exchangeAlias,
      }
    : null
  const fallbackSourceBreadcrumb: BreadcrumbSourceItem = {
    route: localePath('/reviews'),
    labelKey: '82430',
  }
  const resolvedSourceBreadcrumb =
    exchangeSourceBreadcrumb || (sourceBreadcrumb.value?.route ? sourceBreadcrumb.value : fallbackSourceBreadcrumb)
  const items: Array<{ label: string; route?: string; color?: string }> = [
    {
      label: t('82520'),
      route: '/',
      color: '#8C8F97',
    },
  ]

  items.push({
    label: resolveSourceBreadcrumbLabel(resolvedSourceBreadcrumb),
    route: resolvedSourceBreadcrumb.route,
    color: '#8C8F97',
  })

  items.push({
    label: t('84077'),
  })

  return items
})

/** 点击跳转交易商详情前缓存当前页面为来源面包屑。 */
const setExchangeDetailBreadcrumb = () => {
  const targetAlias = feedbackDetailData.value?.webBitWebBottom?.alias
  if (targetAlias) {
    setBreadcrumb(targetAlias, {
      labelKey: '84077',
      route: route.fullPath,
    })
  }
}

/**
 * 解析反馈详情 ID。
 * @param value 路由中的 slug 参数，可能携带 .html 后缀。
 * @returns 传给反馈详情接口的 commentGradeId。
 */
const resolveFeedbackDetailId = (value: unknown) => {
  const rawValue = Array.isArray(value) ? value[0] : value
  return decodeURIComponent(String(rawValue || '')).replace(/\.html$/i, '')
}

/**
 * 格式化反馈发布时间。
 * @param timestamp 兼容秒级和毫秒级时间戳。
 * @returns YYYY-MM-DD 日期文本，非法时间返回 --。
 */
const formatFeedbackDate = (timestamp: number) => {
  const date = new Date(timestamp)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const feedbackDetailId = computed(() => resolveFeedbackDetailId(route.params.review_id))
const currentUserId = computed(() => String(appStore.user?.userId || userIdCookie.value || '').trim())

/** 读取当前反馈详情页的来源面包屑。 */
const refreshSourceBreadcrumb = () => {
  sourceBreadcrumb.value = getBreadcrumb(feedbackDetailId.value)
}

/** 提前拉取举报原因，让举报弹窗打开时直接展示。 */
const loadReportReasons = async () => {
  try {
    const response = await getFeedbackReportReasons()
    reportReasons.value = response?.data?.list || response?.result || []
  } catch {
    reportReasons.value = []
  }
}

const { data: feedbackDetailData, status: feedbackDetailStatus } = await useAsyncData<FeedbackDetailData | null>(
  `feedback-detail:${feedbackDetailId.value}`,
  async () => {
    if (!feedbackDetailId.value) {
      return null
    }

    try {
      const response = await getFeedbackDetail(
        {
          commentGradeId: feedbackDetailId.value,
        },
        {
          headerUserId: currentUserId.value,
        },
      )
      if (!response?.data?.tokenGradeCommentInfo?.content && !response?.data?.tokenGradeCommentInfo?.translateContent) {
        throw createError({
          statusCode: 404,
          statusMessage: 'Page Not Found',
          fatal: true,
        })
      }

      return response?.data ?? {}
    } catch {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
      })
    }
  },
  {
    default: () => null,
  },
)

/** 打开“已解决问题”确认弹窗。如果反馈已处理完毕(isFinishFeedback)，点击无响应拦截；否则弹窗。 */
const handleOpenFeedbackResolvedDialog = () => {
  if (feedbackDetailData.value?.tokenGradeCommentInfo?.isFinishFeedback) {
    return
  }
  isFeedbackResolvedDialogOpen.value = true
}

/** 关闭结案确认弹窗 */
const handleCloseFeedbackResolvedDialog = () => {
  if (isSubmittingResolve.value) {
    return
  }
  isFeedbackResolvedDialogOpen.value = false
}

/** 点击结案确认弹窗的“确定”按钮，提交成功后刷新详情页并自动关闭弹窗 */
const handleConfirmFeedbackResolved = async () => {
  if (isSubmittingResolve.value) {
    return
  }
  isSubmittingResolve.value = true
  try {
    // 1. 发送结案 POST 状态修改请求，通知后端更新状态
    await finishFeedbackGrade({
      userLoginId: appStore.user?.userId || '',
      commentGradeId: feedbackDetailId.value,
    })

    // 2. 成功后，常规客户端请求最新数据以更新组件渲染状态
    const response = await getFeedbackDetail(
      {
        commentGradeId: feedbackDetailId.value,
      },
      {
        headerUserId: currentUserId.value,
      },
    )
    if (response?.data) {
      feedbackDetailData.value = response.data
    }

    // 3. 数据重载成功后，再关闭确认弹层
    isFeedbackResolvedDialogOpen.value = false
  } catch (error) {
    // console.error('更新反馈详情数据失败：', error)
  } finally {
    isSubmittingResolve.value = false
  }
}

const { data: downloadLinksData } = await useAsyncData(
  'feedback-detail:download-links',
  async () => {
    return await getAppDownloadLinks()
  },
  {
    default: () => FALLBACK_DOWNLOAD_LINKS,
  },
)

const downloadLinks = computed(() => downloadLinksData.value ?? FALLBACK_DOWNLOAD_LINKS)

/** 反馈发布时间，兼容秒级和毫秒级时间戳。 */
const feedbackSubmittedAt = computed(() => {
  const publishTime = feedbackDetailData.value?.userInfo?.publishTime

  if (publishTime === undefined || publishTime === null || publishTime === '') {
    return '--'
  }

  const timestamp = Number(publishTime)

  if (!Number.isFinite(timestamp)) {
    return String(publishTime)
  }

  const normalizedTimestamp = timestamp < 1e12 ? timestamp * 1000 : timestamp

  return formatFeedbackDate(normalizedTimestamp)
})

/** 反馈详情图片列表，字段命名参考 FeedbackList.vue 的 images 结构。 */
const feedbackImages = computed(() => {
  const realImages = (feedbackDetailData.value?.tokenGradeCommentInfo?.images || []).flatMap((image: any) =>
    image.list ? [image.list] : [],
  )
  return realImages
})

/** 当前展示的大图，点击缩略图后切换到对应图片。 */
const currentFeedbackImage = computed(
  () => feedbackImages.value[selectedFeedbackImageIndex.value] || feedbackImages.value[0] || '',
)

/** 控制反馈大图预览的显示状态 */
const isFeedbackImagePreviewVisible = ref(false)

/** 反馈地区，优先取 tokenGradeCommentInfo.country。 */
const feedbackCountryText = computed(() => feedbackDetailData.value?.tokenGradeCommentInfo?.country || '--')

/** 反馈右侧类型标签，优先展示接口返回的负面类型。 */
const feedbackTypeTagText = computed(() => feedbackDetailData.value?.tokenGradeCommentInfo?.negativeType)

/** 缩略图滚动容器当前是否在最左/最右。 */
const isThumbnailAtStart = ref(true)
const isThumbnailAtEnd = ref(false)

const updateThumbnailScrollState = () => {
  const scroller = feedbackThumbnailScrollerRef.value
  const track = feedbackThumbnailTrackRef.value
  if (!scroller || !track) return

  isThumbnailAtStart.value = scroller.scrollLeft <= 0
  isThumbnailAtEnd.value = scroller.scrollLeft >= track.scrollWidth - scroller.clientWidth - 1
}

/** 将缩略图向左或向右滚动一个。 */
const scrollFeedbackThumbnails = (direction: 'left' | 'right') => {
  const scroller = feedbackThumbnailScrollerRef.value
  const track = feedbackThumbnailTrackRef.value

  if (!scroller || !track) {
    return
  }

  const maxScrollLeft = Math.max(track.scrollWidth - scroller.clientWidth, 0)
  const currentScrollLeft = Math.max(scroller.scrollLeft, 0)
  let targetScrollLeft = currentScrollLeft

  // 滚动步长为 100px(图宽) + 16px(间距) = 116px
  const scrollStep = 116

  if (direction === 'left') {
    targetScrollLeft = Math.max(currentScrollLeft - scrollStep, 0)
  } else {
    targetScrollLeft = Math.min(currentScrollLeft + scrollStep, maxScrollLeft)
  }

  scroller.scrollTo({
    left: targetScrollLeft,
    behavior: 'smooth',
  })

  // 滚动结束后更新边界状态
  setTimeout(updateThumbnailScrollState, 300)
}

/**
 * 选中缩略图并同步主图。
 * @param index 当前图片在完整图片列表中的下标。
 */
const selectFeedbackImage = (index: number) => {
  selectedFeedbackImageIndex.value = index
}

watch(feedbackImages, () => {
  if (selectedFeedbackImageIndex.value > feedbackImages.value.length - 1) {
    selectedFeedbackImageIndex.value = 0
  }
  updateThumbnailScrollState()
})

onMounted(() => {
  isClientMounted.value = true
  refreshSourceBreadcrumb()
  void loadReportReasons()
})

watch(feedbackDetailId, refreshSourceBreadcrumb)

const isFeedbackDetailReady = computed(
  () => feedbackDetailStatus.value === 'success' || feedbackDetailStatus.value === 'error',
)
const is404 = computed(() => isFeedbackDetailReady.value && !feedbackDetailData.value)

/** 详情数据由 useAsyncData 在 SSR 和客户端复用，TDK 直接消费同一份结果。 */
const headFeedbackDetailData = computed(() => feedbackDetailData.value)
const isHead404 = computed(() => is404.value)

useHead(() => {
  if (isHead404.value) {
    return {
      title: 'Page Not Found',
    }
  }
  const userName = headFeedbackDetailData.value?.userInfo?.nickName ?? ''
  const evaluateName = headFeedbackDetailData.value?.tokenGradeCommentInfo?.evaluateName ?? ''
  const seoFeedbackSummary =
    feedbackDetailData?.value?.tokenGradeCommentInfo?.translateContent ||
    headFeedbackDetailData.value?.tokenGradeCommentInfo?.content ||
    ''

  return {
    title: t('84400', [userName, evaluateName, seoFeedbackSummary]),
    meta: [
      {
        name: 'description',
        content: t('84390', [userName, evaluateName]),
      },
    ],
  }
})
</script>

<template>
  <main class="min-h-screen min-w-[800px] bg-[#F5F6F8] pt-[70px] text-[#292a2e]">
    <!-- 详情请求未完成时只展示骨架屏，完成后再决定展示 404 还是真实页面。 -->
    <FeedbackDetailSkeleton v-if="!isFeedbackDetailReady" />

    <!-- 加载完毕后，再判定是出 404 还是出真数据页面 -->
    <template v-else>
      <CommonNotFound v-if="is404" />
      <div v-else class="mx-auto w-[min(1200px,calc(100vw-48px))] max-[799px]:w-[768px]">
        <CommonBreadcrumb :items="breadcrumbItems" />
        <div class="pb-[113px]">
          <div class="bg-[#FFF]">
            <div
              class="flex flex-col space-y-[24px] bg-[#F5F6F8] min-[1248px]:flex-row min-[1248px]:space-y-[0] min-[1248px]:space-x-[24px]"
            >
              <div class="w-full shrink-0 rounded-[12px] bg-[#FFF] p-[24px] min-[1248px]:w-[792px]">
                <div class="flex flex-col space-y-[16px]">
                  <div class="flex flex-col space-y-[12px]">
                    <div class="flex items-center justify-between">
                      <div class="flex min-w-0 items-center space-x-[8px]">
                        <CommonImage
                          class="shrink-0 rounded-full object-cover"
                          width="40"
                          height="40"
                          loading="eager"
                          :src="
                            feedbackDetailData?.userInfo?.avatarAddress ||
                            'https://resources1.interface003.com/web20/img/discover/40-40.png'
                          "
                        />
                        <div class="flex min-w-0 flex-col space-y-[2px]">
                          <div class="truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]">
                            {{ feedbackDetailData?.userInfo?.nickName }}
                          </div>
                          <div class="text-[11px] leading-[16px] font-[400] text-[#8c8f97]">
                            {{ feedbackSubmittedAt }}
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="feedbackTypeTagText"
                        class="shrink-0 rounded-[2px] border-[0.5px] border-[rgba(11,18,40,0.14)] bg-[#fff] px-[8px] py-[4px] text-[12px] leading-[18px] font-[400] whitespace-nowrap text-[#292a2e]"
                      >
                        {{ feedbackTypeTagText }}
                      </div>
                    </div>
                    <!-- translateContent译文 -->
                    <div class="text-[14px] leading-[22px] font-[400] break-words text-[#292a2e]">
                      {{
                        feedbackDetailData?.tokenGradeCommentInfo?.translateContent ||
                        feedbackDetailData?.tokenGradeCommentInfo?.content
                      }}
                    </div>
                  </div>

                  <!-- content原文 -->
                  <div
                    v-if="
                      feedbackDetailData?.tokenGradeCommentInfo?.content &&
                      feedbackDetailData?.tokenGradeCommentInfo?.languageCode !== locale
                    "
                    class="flex flex-col space-y-[8px]"
                  >
                    <div class="flex items-center space-x-[10px]">
                      <div class="h-[1px] min-w-0 flex-1 bg-[rgba(11,18,40,0.14)]"></div>
                      <div
                        class="flex shrink-0 cursor-pointer items-center space-x-[4px] text-[12px] leading-[18px] font-[400] text-[#8c8f97]"
                        @click="isTranslatedContentVisible = !isTranslatedContentVisible"
                      >
                        <span>{{ t('84076') }}</span>
                        <CommonImage
                          class="transition-transform duration-200"
                          :class="{ 'rotate-180': !isTranslatedContentVisible }"
                          width="14"
                          height="14"
                          loading="eager"
                          src="https://resources1.interface003.com/web30/2026/06/25/28e2268c-1c2b-4526-ac5d-7d7ffb1e4583.png"
                          alt=""
                        />
                      </div>
                    </div>
                    <div
                      v-if="isTranslatedContentVisible"
                      class="text-[14px] leading-[22px] font-[400] break-words text-[#7d818a]"
                    >
                      {{ feedbackDetailData?.tokenGradeCommentInfo?.content }}
                    </div>
                  </div>

                  <div v-if="currentFeedbackImage" class="flex flex-col items-center space-y-[28px]">
                    <div
                      class="flex h-[500px] w-full cursor-pointer items-center justify-center overflow-hidden rounded-[12px] bg-[#f2f3f5]"
                      @click="isFeedbackImagePreviewVisible = true"
                    >
                      <CommonImage
                        class="max-h-[500px] max-w-full rounded-[12px] object-contain"
                        :src="currentFeedbackImage"
                        style="width: auto; max-width: 100%"
                        loading="eager"
                        alt=""
                      />
                    </div>
                    <div
                      v-if="feedbackImages.length > 1"
                      class="flex items-center"
                      :class="feedbackImages.length > 5 ? 'space-x-[16px]' : ''"
                    >
                      <CommonImage
                        v-if="feedbackImages.length > 5"
                        class="shrink-0 rtl:rotate-180"
                        :class="{ 'cursor-pointer': !isThumbnailAtStart }"
                        width="24"
                        height="24"
                        loading="eager"
                        :src="
                          isThumbnailAtStart
                            ? 'https://resources1.interface003.com/web30/2026/06/29/a34587d4-86ba-4d9d-8741-89d05020a8dd.png'
                            : 'https://resources1.interface003.com/web30/2026/06/29/c8ca9d58-9f5e-4a5e-9c95-11e55c10e33a.png'
                        "
                        @click="!isThumbnailAtStart && scrollFeedbackThumbnails('left')"
                        alt=""
                      />
                      <div
                        ref="feedbackThumbnailScrollerRef"
                        class="overflow-hidden scroll-smooth"
                        :class="feedbackImages.length > 5 ? 'w-[564px]' : 'w-max'"
                      >
                        <div ref="feedbackThumbnailTrackRef" class="flex w-max items-center space-x-[16px]">
                          <div
                            v-for="(image, index) in feedbackImages"
                            :key="`${image}-${index}`"
                            class="h-[48px] w-[100px] shrink-0 cursor-pointer overflow-hidden rounded-[4px] border border-[#05152406]"
                            @click="selectFeedbackImage(index)"
                          >
                            <CommonImage
                              class="h-full w-full object-contain opacity-[0.2]"
                              :class="{ '!opacity-100': selectedFeedbackImageIndex === index }"
                              width="100"
                              height="48"
                              loading="eager"
                              :src="image"
                            />
                          </div>
                        </div>
                      </div>
                      <CommonImage
                        v-if="feedbackImages.length > 5"
                        class="shrink-0 rotate-180 rtl:rotate-0"
                        :class="{ 'cursor-pointer': !isThumbnailAtEnd }"
                        width="24"
                        height="24"
                        loading="eager"
                        :src="
                          isThumbnailAtEnd
                            ? 'https://resources1.interface003.com/web30/2026/06/29/a34587d4-86ba-4d9d-8741-89d05020a8dd.png'
                            : 'https://resources1.interface003.com/web30/2026/06/29/c8ca9d58-9f5e-4a5e-9c95-11e55c10e33a.png'
                        "
                        @click="!isThumbnailAtEnd && scrollFeedbackThumbnails('right')"
                        alt=""
                      />
                    </div>
                  </div>

                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-[4px]">
                      <CommonImage
                        width="14"
                        height="14"
                        loading="eager"
                        src="https://resources1.interface003.com/web30/2026/06/29/b7514b58-a956-4fb2-a195-d72e0134f9c4.png"
                        alt=""
                      />
                      <span class="text-[12px] leading-[18px] font-[400] text-[#8c8f97]">{{
                        feedbackCountryText
                      }}</span>
                    </div>
                    <div
                      class="cursor-pointer text-[12px] leading-[18px] font-[400] text-[#0d3f63]"
                      @click="isReportDialogOpen = true"
                    >
                      {{ t('83782') }}
                    </div>
                  </div>
                  <div
                    v-if="feedbackDetailData?.tokenGradeCommentInfo?.isFinishFeedback || feedbackDetailData?.isSelf"
                    class="flex w-full items-center space-x-[12px] rounded-[8px] bg-[rgba(31,132,90,0.06)] p-[12px]"
                    :class="{ 'cursor-pointer': !feedbackDetailData?.tokenGradeCommentInfo?.isFinishFeedback }"
                    @click="handleOpenFeedbackResolvedDialog"
                  >
                    <div class="flex min-w-0 flex-1 items-center space-x-[12px]">
                      <CommonImage
                        class="shrink-0"
                        width="24"
                        height="24"
                        loading="eager"
                        :src="
                          feedbackDetailData?.tokenGradeCommentInfo?.isFinishFeedback
                            ? 'https://resources1.interface003.com/web30/2026/07/09/bfb5a9df-5b78-40ce-b03e-ec2ce787d506.png'
                            : 'https://resources1.interface003.com/web30/2026/07/08/2f8e38f7-c08f-47a2-ad2a-d8d723810d45.png'
                        "
                      />
                      <div class="min-w-0 flex-1 text-[12px] leading-[18px] font-[400] break-words text-[#1f845a]">
                        {{ feedbackDetailData?.tokenGradeCommentInfo?.isFinishFeedback ? t('83795') : t('84592') }}
                      </div>
                    </div>
                    <CommonImage
                      v-if="!feedbackDetailData?.tokenGradeCommentInfo?.isFinishFeedback"
                      class="shrink-0 rtl:rotate-180"
                      width="16"
                      height="16"
                      loading="eager"
                      src="https://resources1.interface003.com/web30/2026/07/08/153589c3-921e-4e3b-8b80-479ea3c552eb.png"
                    />
                  </div>
                  <div class="flex w-full items-center pb-[8px]">
                    <NuxtLink
                      v-if="feedbackDetailData?.tokenGradeCommentInfo?.lastPage?.content"
                      :to="localePath(`/reviews/${feedbackDetailData.tokenGradeCommentInfo.lastPage.commentGradeId}`)"
                      target="_blank"
                      class="flex min-w-0 flex-1 cursor-pointer flex-col items-start rounded-[12px] p-[20px] hover:bg-[#f8f8f8]"
                    >
                      <div class="text-[14px] leading-[20px] font-[400] whitespace-nowrap text-[#a3a3a3]">
                        {{ t('83780') }}
                      </div>
                      <div class="mt-[8px] line-clamp-3 text-[14px] leading-[22px] font-[400] text-[#3d3d3d]">
                        {{ feedbackDetailData.tokenGradeCommentInfo.lastPage.content }}
                      </div>
                    </NuxtLink>
                    <NuxtLink
                      v-if="feedbackDetailData?.tokenGradeCommentInfo?.nextPage?.content"
                      :to="localePath(`/reviews/${feedbackDetailData.tokenGradeCommentInfo.nextPage.commentGradeId}`)"
                      target="_blank"
                      class="flex min-w-0 flex-1 cursor-pointer flex-col items-start rounded-[12px] p-[20px] hover:bg-[#f8f8f8]"
                    >
                      <div
                        class="text-[14px] leading-[20px] font-[400] whitespace-nowrap text-[#a3a3a3]"
                        :class="{ 'self-end': feedbackDetailData?.tokenGradeCommentInfo?.lastPage?.content }"
                      >
                        {{ t('83781') }}
                      </div>
                      <div class="mt-[8px] line-clamp-3 text-[14px] leading-[22px] font-[400] text-[#3d3d3d]">
                        {{ feedbackDetailData.tokenGradeCommentInfo.nextPage.content }}
                      </div>
                    </NuxtLink>
                  </div>

                  <NuxtLink
                    :to="
                      localePath(
                        feedbackDetailData?.webBitWebBottom?.alias
                          ? `/exchanges/${feedbackDetailData.webBitWebBottom.alias}`
                          : '/',
                      )
                    "
                    target="_blank"
                    class="flex cursor-pointer items-center rounded-[12px] border-[0.5px] border-[rgba(11,18,40,0.14)] px-[16px] py-[12px] transition-colors duration-200"
                    @click="setExchangeDetailBreadcrumb"
                  >
                    <div class="flex min-w-0 flex-1 items-center space-x-[8px]">
                      <CommonImage
                        class="shrink-0 rounded-[4px] object-cover"
                        :src="feedbackDetailData?.tokenGradeCommentInfo?.evaluateIcon"
                        width="32"
                        height="32"
                        loading="eager"
                      />
                      <div class="truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]">
                        {{ feedbackDetailData?.tokenGradeCommentInfo?.evaluateName }}
                      </div>
                    </div>
                    <div
                      class="min-w-0 flex-1 text-center text-[14px] leading-[22px] font-[400]"
                      :style="{ color: aRgbToRgba(feedbackDetailData?.webBitWebBottom?.labels?.[0]?.color ?? '') }"
                    >
                      {{ feedbackDetailData?.webBitWebBottom?.labels?.[0]?.name || '--' }}
                    </div>
                    <div class="min-w-0 flex-1 text-center text-[14px] leading-[22px] font-[400] text-[#292A2E]">
                      {{ feedbackDetailData?.webBitWebBottom?.labels?.[1]?.name || '--' }}
                    </div>
                    <div class="flex min-w-0 flex-1 items-center justify-end">
                      <div
                        class="flex items-center justify-center space-x-[4px] rounded-[1000px] bg-[#f8f8f8] px-[12px] py-[4px]"
                      >
                        <CommonImage
                          src="https://resources1.interface003.com/web30/2026/06/29/ae8055ec-19bb-4765-9088-a60aff5cbc4d.png"
                          width="14"
                          height="14"
                          loading="eager"
                          alt=""
                        />
                        <span class="text-[14px] leading-[22px] font-[500] whitespace-nowrap text-[#292a2e]">{{
                          feedbackDetailData?.webBitWebBottom?.score || '--'
                        }}</span>
                      </div>
                    </div>
                  </NuxtLink>
                </div>
              </div>
              <FeedbackDetailAside :feedback-detail-data="feedbackDetailData" :download-links="downloadLinks" />
            </div>
          </div>
        </div>
      </div>
      <FeedbackReportDialog
        v-model="isReportDialogOpen"
        :posts-id="feedbackDetailData?.tokenGradeCommentInfo?.commentGradeId ?? ''"
        :report-reasons="reportReasons"
      />
      <CommonImagePreviewDialog
        v-model="isFeedbackImagePreviewVisible"
        :images="feedbackImages"
        :initial-index="selectedFeedbackImageIndex"
      />
      <ElDialog
        v-model="isFeedbackResolvedDialogOpen"
        width="min(327px, calc(100vw - 48px))"
        align-center
        append-to-body
        destroy-on-close
        :show-close="false"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :z-index="3010"
        class="feedback-resolved-dialog"
        modal-class="feedback-resolved-dialog-mask"
        aria-labelledby="feedback-resolved-dialog-title"
      >
        <div class="flex w-full flex-col items-center bg-[#fff] px-[32px] pt-[20px] pb-[32px]">
          <div class="relative flex w-full flex-col items-center">
            <CommonImage
              class="shrink-0"
              src="https://resources1.interface003.com/web30/2026/07/08/ae9d29a4-582e-401c-9581-d1f5faac5916.png"
              width="90"
              height="80"
              loading="eager"
              alt=""
            />
            <div
              id="feedback-resolved-dialog-title"
              class="w-full rounded-[8px] p-[12px] text-center text-[16px] leading-[24px] font-[500] break-words text-[#292a2e]"
            >
              {{ t('84699') }}
            </div>
            <button
              type="button"
              class="absolute top-[12px] right-[0] flex h-[32px] w-[32px] cursor-pointer items-center justify-center border-0 bg-transparent p-[0] rtl:right-auto rtl:left-[0]"
              @click="handleCloseFeedbackResolvedDialog"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/07/08/a8806bf9-0966-485f-8263-7ab6c82bc0de.png"
                width="32"
                height="32"
                loading="eager"
                alt=""
              />
            </button>
          </div>
          <button
            type="button"
            class="hover-mask-effect mt-[20px] flex h-[46px] w-[184px] cursor-pointer items-center justify-center rounded-[100px] border-0 bg-[#112D73] px-[64px] py-[12px] text-center text-[14px] leading-[22px] font-[600] whitespace-nowrap text-[#FFF]"
            :disabled="isSubmittingResolve"
            @click="handleConfirmFeedbackResolved"
          >
            {{ t('82922') }}
          </button>
        </div>
      </ElDialog>
    </template>
  </main>
</template>

<style scoped lang="less">
:global(.feedback-resolved-dialog-mask) {
  background: rgba(9, 20, 51, 0.48);
}

:global(.feedback-resolved-dialog) {
  max-height: calc(100vh - 64px);
  overflow: hidden;
  margin: 0;
  border-radius: 28px;
  background: #fff;
  padding: 0;
  box-shadow: none;
}

:global(.feedback-resolved-dialog .el-dialog__header) {
  display: none;
  padding: 0;
  margin: 0;
}

:global(.feedback-resolved-dialog .el-dialog__body) {
  padding: 0;
}

.hover-mask-effect {
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(255, 255, 255, 0.08);
    opacity: 0;
    transition: all 0.2s ease;
    pointer-events: none;
    border-radius: inherit;
  }

  // 悬浮状态：显示白色 8% 蒙层
  &:hover:not([disabled]):not(.disabled)::after {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.08);
  }

  // 按下状态：以 0.2 秒的平滑过渡切换并显示黑色 10% 蒙层
  &:active:not([disabled]):not(.disabled)::after {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.1);
  }

  // 禁用状态交互：背景变浅蓝，指针变为禁选
  &[disabled] {
    background-color: #afc5fa !important;
    cursor: not-allowed !important;
  }
}
</style>
